import { formatTime } from "../../UI/DesignStandards";

export const SQUAD_DATE_PREFIX = "date:";

export const squadDateLabel = (squadDate) => {
  if (!squadDate) return "Squad";
  const [year, month, day] = String(squadDate).split("-").map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

export const squadFilterValue = (filters = {}) => {
  if (filters.squad && filters.squad !== "all") return filters.squad;
  if (filters.date && filters.date !== "all") {
    return `${SQUAD_DATE_PREFIX}${filters.date}`;
  }
  return "all";
};

export const squadFilterFromValue = (value) => {
  if (!value || value === "all") return { squad: "all", date: "all" };
  if (String(value).startsWith(SQUAD_DATE_PREFIX)) {
    return {
      squad: "all",
      date: String(value).slice(SQUAD_DATE_PREFIX.length),
    };
  }
  return { squad: value, date: "all" };
};

export const groupSquadsByDate = (squads = []) => {
  const groups = [];
  const indexByDate = new Map();
  (squads || []).forEach((squad) => {
    const date = squad.squadDate || "";
    if (!indexByDate.has(date)) {
      indexByDate.set(date, groups.length);
      groups.push({ date, squads: [] });
    }
    groups[indexByDate.get(date)].squads.push(squad);
  });
  groups.sort((left, right) => {
    if (!left.date && !right.date) return 0;
    if (!left.date) return 1;
    if (!right.date) return -1;
    return String(left.date).localeCompare(String(right.date));
  });
  groups.forEach((group) => {
    group.squads.sort((left, right) =>
      String(left.squadTime || "").localeCompare(String(right.squadTime || ""))
    );
  });
  return groups;
};

export const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending-scores", label: "Needs scores" },
  { id: "pending-verification", label: "Ready to verify" },
  { id: "verified", label: "Verified" },
];

export const emptyStatusCounts = () => ({
  all: 0,
  "pending-scores": 0,
  "pending-verification": 0,
  verified: 0,
});

export const squadTimeLabel = (squadTime) => {
  const time = formatTime(squadTime);
  return time === "N/A" ? "Time not set" : time;
};

const cleanLabel = (value) => String(value || "").trim();

export const squadEventLabel = (squad) => {
  const names = Array.isArray(squad?.eventNames) ? squad.eventNames : [];
  const fromNames = names.map(cleanLabel).filter(Boolean);
  if (fromNames.length) return fromNames.join(" · ");
  const nested = squad?.tournamentEvents;
  const events = Array.isArray(nested) ? nested : nested ? [nested] : [];
  const fromEvents = events
    .map((event) => cleanLabel(event?.eventName))
    .filter(Boolean);
  if (fromEvents.length) return fromEvents.join(" · ");
  return cleanLabel(squad?.eventName);
};

export const squadCenterLabel = (squad) =>
  cleanLabel(
    squad?.centerName ||
      squad?.bowlingCenters?.centerName ||
      squad?.bowlingCenter?.centerName
  );

const countBy = (items, keyFor) => {
  const counts = new Map();
  (items || []).forEach((item) => {
    const key = keyFor(item);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
};

export const squadSelectOptionLabel = (
  squad,
  squads = [],
  { groupedByDate = false } = {}
) => {
  const time = squadTimeLabel(squad?.squadTime);
  const date = squad?.squadDate ? squadDateLabel(squad.squadDate) : "";
  const dateTimeKey = `${squad?.squadDate || ""}\t${time}`;
  const timeCounts = countBy(squads, (item) => squadTimeLabel(item?.squadTime));
  const dateTimeCounts = countBy(
    squads,
    (item) => `${item?.squadDate || ""}\t${squadTimeLabel(item?.squadTime)}`
  );
  const timeCollides = (timeCounts.get(time) || 0) > 1;
  const dateTimeCollides = (dateTimeCounts.get(dateTimeKey) || 0) > 1;
  const showDate = Boolean(date) && (timeCollides || !groupedByDate);
  const extras = [];
  if (dateTimeCollides) {
    const peers = (squads || []).filter(
      (item) =>
        `${item?.squadDate || ""}\t${squadTimeLabel(item?.squadTime)}` ===
        dateTimeKey
    );
    const eventLabel = squadEventLabel(squad);
    const centerLabel = squadCenterLabel(squad);
    const eventVaries =
      new Set(peers.map((item) => squadEventLabel(item))).size > 1;
    const centerVaries =
      new Set(peers.map((item) => squadCenterLabel(item))).size > 1;
    if (eventVaries && eventLabel) extras.push(eventLabel);
    if (centerVaries && centerLabel) extras.push(centerLabel);
  }
  return [showDate ? date : "", time, ...extras].filter(Boolean).join(" · ");
};

export const squadPickerLabel = (squad) => {
  if (!squad) return "";
  if (!squad.squadDate) return squadTimeLabel(squad.squadTime);
  return `${squadDateLabel(squad.squadDate)} · ${squadTimeLabel(
    squad.squadTime
  )}`;
};
